import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import type { CatalogLinksIntentState, LinkDraft } from './types'

/**
 * Намерение человека в окне «Связи товаров»: черновик связывания и ресурс списка.
 * Связей и товаров здесь НЕТ — они в кэше RTK Query. Срок жизни — `screen`.
 */
export const EMPTY_LINK_DRAFT: LinkDraft = {
  firstResourceId: '',
  firstKey: '',
  secondResourceId: '',
  secondKey: '',
  productName: '',
  reason: '',
  operationId: null,
}

const initialState: CatalogLinksIntentState = { draft: EMPTY_LINK_DRAFT, listResourceId: '' }

const catalogLinksSlice = createSlice({
  name: 'platform/catalogLinks',
  initialState,
  reducers: {
    /** Любая правка черновика — новое намерение: прежний operationId больше не годится. */
    linkDraftPatched(state, action: PayloadAction<Partial<Omit<LinkDraft, 'operationId'>>>) {
      state.draft = { ...state.draft, ...action.payload, operationId: null }
    },
    /** Нажатие «Связать»: ключ операции закрепляется за черновиком (повтор того же — тот же ключ). */
    linkOperationAssigned(state, action: PayloadAction<string>) {
      if (state.draft.operationId === null) state.draft.operationId = action.payload
    },
    /** Связано: карточки очищаются, ресурсы и причина остаются — следующая пара обычно из тех же ресурсов. */
    linkCompleted(state, action: PayloadAction<string>) {
      // Человек успел поправить черновик, пока шёл запрос: его новый ввод не стирается итогом прежней операции.
      if (state.draft.operationId !== action.payload) return
      state.draft = { ...state.draft, firstKey: '', secondKey: '', productName: '', operationId: null }
    },
    listResourceChosen(state, action: PayloadAction<string>) {
      state.listResourceId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      const rid = action.payload.resourceId
      if (state.draft.firstResourceId === rid || state.draft.secondResourceId === rid) state.draft = EMPTY_LINK_DRAFT
      if (state.listResourceId === rid) state.listResourceId = ''
    })
  },
})

export const { linkDraftPatched, linkOperationAssigned, linkCompleted, listResourceChosen } = catalogLinksSlice.actions
export const catalogLinksReducer = catalogLinksSlice.reducer
